import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../../api/client'
import { CVE_SYNC_KEY, cveSyncQuery, startCveSync, type CveSyncRun, type CveSyncStatus } from '../../api/cveSync'
import { SettingsCheck } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'
import { toast } from '../../ui/notify'
import { CveListPanel } from './CveListPanel'

/**
 * Settings > Softsee > CVE database: de unde vin datele de vulnerabilitati
 * (NVD, apoi KEV si EPSS) si butonul "Sync now", ca un administrator de
 * platforma sa nu astepte cronul de la 3:17 sau echipa de server.
 *
 * Serverul porneste rularea si raspunde imediat; feliile din NVD pleaca dupa
 * raspuns. Ecranul intreaba starea la cateva secunde cat timp merge ceva
 * (`in_progress`) si se opreste singur cand s-a terminat.
 */
export function CveDatabasePanel() {
  const status = useQuery(cveSyncQuery())
  if (status.error) {
    const code = status.error instanceof ApiError ? status.error.status : 0
    return (
      <div className="alert alert-danger" role="alert">
        {code === 403 ? 'You do not have access to this action.' : status.error.message}
      </div>
    )
  }
  if (!status.data) return <Spinner />
  /* Cartonasul de stare in stanga, lista CVE-urilor in dreapta; pe un ecran
     ingust lista coboara sub cartonas (flex-wrap, fara reguli de latime). */
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }} data-testid="cve-database-layout">
      <div style={{ flex: '0 1 520px', minWidth: 0, maxWidth: '100%' }}>
        <CveDatabaseCard status={status.data} />
      </div>
      <div style={{ flex: '1 1 560px', minWidth: 0, maxWidth: '100%' }}>
        <CveListPanel />
      </div>
    </div>
  )
}

const TONE: Record<string, string> = { ok: 'alert-success', warn: 'alert-warning', bad: 'alert-danger' }

const STEP_LABEL: Record<string, string> = { nvd: 'CVE catalog', kev: 'CISA KEV', epss: 'EPSS', scan: 'Device scan' }

function count(value: number | null): string {
  return value === null ? 'not loaded' : value.toLocaleString('en-US')
}

export function CveDatabaseCard({ status }: { status: CveSyncStatus }) {
  const client = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [followUp, setFollowUp] = useState(true)
  const running = status.in_progress
  const run = status.run

  const onSync = async () => {
    if (busy || running) return
    setBusy(true)
    try {
      const started = await startCveSync(followUp)
      client.setQueryData<CveSyncStatus>(CVE_SYNC_KEY, started)
      toast(started.message || 'The sync has started.', 'CVE database', 'success')
    } catch (err) {
      toast(err instanceof Error && err.message ? err.message : 'The sync did not start.', 'Error', 'error')
      void client.invalidateQueries({ queryKey: CVE_SYNC_KEY })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="panel panel-default" style={{ maxWidth: 760 }} data-testid="cve-database">
      <div className="panel-heading">
        <strong>CVE database</strong> - the vulnerability data every Softsee and CMDB screen reads
      </div>
      <div className="panel-body">
        <div className={`alert ${TONE[status.line.tone] ?? 'alert-info'}`} role="status" data-testid="cve-line">
          <div>{status.line.text}</div>
          {status.line.hint !== '' && <div style={{ fontSize: 12, marginTop: 4 }}>{status.line.hint}</div>}
        </div>

        <table className="table table-condensed" style={{ maxWidth: 520 }}>
          <tbody>
            <tr>
              <th scope="row">Last sync run</th>
              <td data-testid="cve-last-run">{status.state.last_run_at ?? 'never'}</td>
            </tr>
            <tr>
              <th scope="row">Last server message</th>
              <td>{status.state.last_message !== '' ? status.state.last_message : '-'}</td>
            </tr>
            <tr>
              <th scope="row">Vulnerabilities</th>
              <td data-testid="cve-count">{count(status.counts.cves)}</td>
            </tr>
            <tr>
              <th scope="row">Known exploited (CISA KEV)</th>
              <td>{count(status.counts.kev)}</td>
            </tr>
            <tr>
              <th scope="row">Exploit scores (EPSS)</th>
              <td>{count(status.counts.epss)}</td>
            </tr>
          </tbody>
        </table>

        {run !== null && <RunSummary run={run} />}

        {!status.configured && (
          <p className="text-danger" data-testid="cve-not-configured">
            The cron server is not configured on this instance (CRONJOBS_SERVER), so the sync cannot be started from here.
          </p>
        )}

        <div className="form-group">
          <SettingsCheck
            id="cve-follow-up"
            checked={followUp}
            disabled={running || busy}
            onChange={(on: boolean) => setFollowUp(on)}
            label="Then refresh KEV and EPSS, and match the devices again"
          />
        </div>

        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void onSync()}
          disabled={running || busy || !status.configured}
          data-testid="cve-sync-now"
        >
          {running ? (
            <>
              <i className="fas fa-sync fa-spin" aria-hidden="true"></i> Syncing...
            </>
          ) : (
            <>
              <i className="fas fa-sync" aria-hidden="true"></i> Sync now
            </>
          )}
        </button>
        <p className="text-muted" style={{ fontSize: 12, marginTop: 8 }}>
          The first full load brings the whole CVE catalog and takes tens of minutes, in slices; later runs bring only
          what changed. The nightly job (3:17) keeps running as before.
        </p>
      </div>
    </div>
  )
}

function RunSummary({ run }: { run: CveSyncRun }) {
  const title =
    run.state === 'running'
      ? `Running: ${STEP_LABEL[run.step] ?? run.step}, ${run.slices} slice${run.slices === 1 ? '' : 's'} so far`
      : run.state === 'done'
        ? 'Last sync from this screen finished'
        : run.state === 'failed'
          ? 'Last sync from this screen failed'
          : run.state === 'busy'
            ? 'Another sync was already running on the server'
            : run.state === 'stalled'
              ? 'Last sync from this screen stopped without finishing'
              : `Last sync: ${run.state}`

  return (
    <div className="well well-sm" data-testid="cve-run">
      <div>
        <strong>{title}</strong>
        <span className="text-muted">
          {' '}
          - started by {run.requested_by || 'unknown'}
          {run.requested_at ? ` at ${run.requested_at}` : ''}
          {run.finished_at ? `, finished ${run.finished_at}` : ''}
        </span>
      </div>
      {run.state === 'running' && run.message !== '' && <div data-testid="cve-run-progress">{run.message}</div>}
      {run.error !== '' && (
        <div className="text-danger" data-testid="cve-run-error">
          {run.error}
        </div>
      )}
      {run.steps.length > 0 && (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {run.steps.map((step, i) => (
            <li key={`${step.step}-${i}`} className={step.ok ? undefined : 'text-danger'}>
              <i className={step.ok ? 'fas fa-check text-success' : 'fas fa-times'} aria-hidden="true"></i> {step.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
